import { Injectable } from '@angular/core';
import {
    FormControl,
    FormGroupDirective,
    NgForm,
    UntypedFormGroup,
    ValidationErrors,
    ValidatorFn,
} from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { validateIBAN } from 'ngx-iban-validator';

@Injectable()
export class ValidationService {
    static usernameValidator(control) {
        if (!control.value) {
            return null;
        }

        if (control.value.match(/^[\w.-]*$/)) {
            return null;
        } else {
            return { invalidUserName: true };
        }
    }

    static emailValidator(control) {
        // tslint:disable:max-line-length
        if (
            control.value?.match(
                /^(([^\s"(),.:;<>@[\\\]]+(\.[^\s"(),.:;<>@[\\\]]+)*)|(".+"))@((\[(?:\d{1,3}\.){3}\d{1,3}])|(([\dA-Za-z\-]+\.)+[A-Za-z]{2,}))$/,
            ) &&
            !control.value?.match(/[äöüßÄÖÜ]/)
        ) {
            // tslint:enable:max-line-length
            return null;
        } else {
            return { invalidEmail: true };
        }
    }

    static customerNumberValidator(control) {
        if (control.value.match(/^\d{8,10}$/)) {
            return null;
        } else {
            return { invalidCustomerNumber: true };
        }
    }

    static supplierNumberValidator(control) {
        if (!control.value) {
            return null;
        }

        if (control.value.match(/^L\d{9}$/) || control.value.match(/^\d{7,10}$/)) {
            return null;
        } else {
            return { invalidSupplierNumber: true };
        }
    }

    static phoneNumberValidator(control) {
        if (!control.value) {
            return null;
        }

        if (control.value.match(/^[\d\s()+/\-]*$/)) {
            return null;
        } else {
            return { invalidPhoneNumber: true };
        }
    }

    static phoneNumberE164Validator(control) {
        if (!control.value) {
            return null;
        }

        if (control.value.match(/^\+[1-9]\d{8,14}$/)) {
            return null;
        } else {
            return { invalidPhoneNumber: true };
        }
    }

    static onlyDigitsValidator(control) {
        if (!control.value) {
            return null;
        }

        if (control.value.match(/^\d*$/)) {
            return null;
        } else {
            return { onlyDigitsAllowed: true };
        }
    }

    static PasswordValidator(control) {
        if (!control.value) {
            return null;
        }

        if (control.value.match(/^(?:(.)(?!\1{3,})){8,}$/)) {
            return null;
        } else {
            return { invalidPassword: true };
        }
    }

    static ibanValidator(control) {
        if (!control.value) {
            return null;
        }

        if (validateIBAN(control)?.ibanInvalid) {
            return { invalidIban: true };
        } else {
            return null;
        }
    }

    // Forbid following special characters in names and more than one space
    static specialCharacterValidator(control) {
        if (!control.value?.match(/[!@#$%^&*+=\[\]{};':"\\|,<>?]|\s{2,}/)) {
            return null;
        } else {
            return { invalidName: true };
        }
    }

    // Forbid only whitespaces in input fields
    static onlyWhiteSpaceValidator(control) {
        if (!!control?.value?.trim()?.length) {
            return null;
        } else {
            return { onlyWhiteSpace: true };
        }
    }

    static viessmannDomainValidator(redirect: string): string {
        // Allow only https:// protocol
        if (!redirect?.startsWith('https://')) {
            return undefined;
        }

        try {
            // check if redirect is a valid URL
            const url = new URL(redirect);
            return url.origin.endsWith('.viessmann.com') ? redirect : undefined;
        } catch (e) {
            // redirect is no valid URL
            return undefined;
        }
    }

    /** check if values of given controls within the same form are matching */
    static inputConfirmationValidator(formControlName1: string, formControlName2: string): ValidatorFn {
        return (form: UntypedFormGroup): ValidationErrors | null => {
            const value1 = form.get(formControlName1).value;
            const value2 = form.get(formControlName2).value;
            return value1 === value2 ? null : { noMatch: true };
        };
    }
}

/** Show error on touched control when form has a specific error */
export class InvalidFormErrorStateMatcher implements ErrorStateMatcher {
    constructor(private errorToCheck: string) {}

    isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
        return form.hasError(this.errorToCheck) && control.touched;
    }
}
